<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { EntityType } from '@/types/enums';
import { getEntityTypeIcon, getEntityTypeTranslation, narrowToEntityType, toTitleCase } from '@/utils/kyc';

interface Props {
  entityType: EntityType | string | null | undefined;
  iconOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  iconOnly: false,
});

const resolvedEntityType = computed(() => narrowToEntityType(props.entityType) ?? null);

const displayLabel = computed(() => {
  if (resolvedEntityType.value) {
    return toTitleCase(getEntityTypeTranslation(resolvedEntityType.value));
  }

  const fallbackLabel = String(props.entityType ?? '').trim();
  return fallbackLabel ? toTitleCase(fallbackLabel) : 'Entidade';
});

const displayIcon = computed(() => getEntityTypeIcon(resolvedEntityType.value));

const tagClass = computed(() => {
  const baseClass = 'inline-flex items-center rounded-full border text-xs font-semibold shadow-sm';
  const spacingClass = props.iconOnly ? 'px-2 py-1' : 'gap-2 px-2.5 py-1';

  if (resolvedEntityType.value === 'person') {
    return `${baseClass} ${spacingClass} border-blue-200 bg-blue-50 text-blue-700`;
  }

  if (resolvedEntityType.value === 'company') {
    return `${baseClass} ${spacingClass} border-amber-200 bg-amber-50 text-amber-700`;
  }

  return `${baseClass} ${spacingClass} border-surface-200 bg-surface-50 text-surface-700`;
});

const iconWrapperClass = computed(() => {
  const baseClass = `flex items-center justify-center rounded-full ${props.iconOnly ? 'h-6 w-6' : 'h-5 w-5'}`;

  if (resolvedEntityType.value === 'person') {
    return `${baseClass} bg-blue-100`;
  }

  if (resolvedEntityType.value === 'company') {
    return `${baseClass} bg-amber-100`;
  }

  return `${baseClass} bg-surface-100`;
});

const iconClass = computed(() => {
  if (resolvedEntityType.value === 'person') return 'text-blue-600';
  if (resolvedEntityType.value === 'company') return 'text-amber-600';
  return 'text-surface-500';
});

const iconSize = computed(() => (props.iconOnly ? 'sm' : 'xs'));
</script>

<template>
  <span
    :class="tagClass"
    :title="props.iconOnly ? displayLabel : undefined"
    :aria-label="props.iconOnly ? displayLabel : undefined"
  >
    <span :class="iconWrapperClass">
      <BaseIcon :size="iconSize" :class="iconClass">
        {{ displayIcon }}
      </BaseIcon>
    </span>
    <span v-if="!props.iconOnly">{{ displayLabel }}</span>
  </span>
</template>
